export class LSystem
{
    axiom: string = "AB";
    alphabet: string = "ABCF";
    constants: string = "+-[]";
    rules: {[character: string]: string} = {};

    forward: string = "F";
    left: string = "-";
    right: string = "+";
    push: string = "[";
    pop: string = "]";
    angle_deg: number = 25.0;
    length_px: number = 20.0;
    terminator: string = ".";

    stringForChar(character: string) : string
    {
        if (character in this.rules)
        {
            return this.rules[character].split(this.terminator, 2)[0];
        }

        return character;
    }

    generation(i : number): string
    {
        if (i <= 0) return this.axiom;

        let prevGeneration : string = this.generation(i - 1);

        let nextGeneration : string = "";

        let n = prevGeneration.length;
        for (let i = 0; i < n; i++)
        {
            let character : string = prevGeneration.charAt(i);

            if (character == this.terminator) return nextGeneration;

            nextGeneration += this.stringForChar(character);
        }

        return nextGeneration;
    }

    linesForGeneration(generationIndex: number, x0: number, y0: number, angle0_deg: number) : number[][]
    {
        let initialDrawState = new DrawState(x0, y0, angle0_deg);
        let drawState: DrawState = initialDrawState.clone();
        let drawStack: DrawState[] = [initialDrawState.clone()];

        let generationStr: string = this.generation(generationIndex);
        let n : number = generationStr.length;
        let lineSegments: number[][] = [];

        for (let i = 0; i < n; i++)
        {
            let character: string = generationStr.charAt(i);
            if (character == this.push)
            {
                drawStack.push(drawState.clone());
            } 
            else if (character == this.pop)
            {
                let state = drawStack.pop();
                drawState = (state) ? state : initialDrawState.clone();
            }
            else if (character == this.forward)
            {
                let x0 = drawState.x;
                let y0 = drawState.y;
                let angle_rad = drawState.angle_rad();
                let x1 = x0 + this.length_px * Math.cos(angle_rad);
                let y1 = y0 + this.length_px * Math.sin(angle_rad);
                let segment: number[] = [x0, y0, x1, y1];
                drawState.x = x1;
                drawState.y = y1;
                lineSegments.push(segment);
            }
            else if (character == this.left)
            {
                drawState.angle_deg += this.angle_deg;
            }
            else if (character == this.right)
            {
                drawState.angle_deg -= this.angle_deg;
            }
            else if (character == this.terminator)
            {
                break;
            }
        }

        return lineSegments;

    }

    genome(): LSystemGenome
    {
        let genome: LSystemGenome = new LSystemGenome(10, this.alphabet, this.constants);
        genome.axiom = this.axiom.slice();
        genome.rules = {};
        for (let key in this.rules)
        {
            genome.rules[key] = this.rules[key].slice();
        }
        genome.normalise();
        return genome;
    }

    updateFromGenome(genome: LSystemGenome)
    {
        this.axiom = genome.axiom.slice();
        this.rules = {};
        for (let key in genome.rules)
        {
            this.rules[key] = genome.rules[key].slice();
        }
    }
}

export class LSystemGenome
{
    TERMINATOR : string = ".";

    axiom: string = "X";
    alphabet: string = "XF";
    constants: string = "+-[]";
    rules: {[character: string]: string} = 
        {
            "X" : "F+[[X]-X]-F[-FX]+X",
            "F" : "FF"
        };

    angle_deg: number = 25.0;
    length_px: number = 5.0;

    MAX_AXIOM_LENGTH: number;
    SYMBOLS(): string 
    {
        return this.alphabet + this.constants + ".";
    }

    constructor(maxAxiomLength: number, alphabet: string, constants: string)
    {
        this.MAX_AXIOM_LENGTH = maxAxiomLength;
        this.alphabet = alphabet;
        this.constants = constants;
        this.randomise();
    }

    randomise()
    {
        this.axiom = randomStringFrom(this.SYMBOLS(), this.MAX_AXIOM_LENGTH);

        const ALPHABET_SIZE: number = this.alphabet.length;
        this.rules = {};
        for (let i = 0; i < ALPHABET_SIZE; i++)
        {
            this.rules[this.alphabet[i]] = randomStringFrom(this.SYMBOLS(), this.MAX_AXIOM_LENGTH);
        }
    }

    // pad out axiom and rules to full length if necessary
    normalise()
    {
        this.axiom += this.TERMINATOR;
        this.axiom = paddedString(this.axiom, this.SYMBOLS(), this.MAX_AXIOM_LENGTH);

        const ALPHABET_SIZE: number = this.alphabet.length;
        for (let i = 0; i < ALPHABET_SIZE; i++)
        {
            this.rules[this.alphabet[i]] += this.TERMINATOR;
            this.rules[this.alphabet[i]] = paddedString(this.rules[this.alphabet[i]], this.SYMBOLS(), this.MAX_AXIOM_LENGTH);
        }        
    }
}

function randomStringFrom(s: string, n: number): string
{
    let outputString: string = "";
    for (let i = 0; i < n; i++)
    {
        let iSource : number = Math.floor(Math.random() * s.length);
        outputString += s[iSource];      
    }
    return outputString;
}

function paddedString(originalString: string, symbols: string, maxLength: number)
{
    let outputString: string = originalString;
    for (let i = originalString.length; i < maxLength; i++)
    {
        let iSource : number = Math.floor(Math.random() * symbols.length);
        outputString += symbols[iSource];      
    }
    return outputString;    
}

class DrawState
{
    x: number;
    y: number;
    angle_deg: number;

    constructor(x: number, y: number, angle_deg: number)
    {
        this.x = x;
        this.y = y;
        this.angle_deg = angle_deg;
    }

    clone(): DrawState
    {
        return new DrawState(this.x, this.y, this.angle_deg);
    }

    angle_rad() : number
    {
        return this.angle_deg * Math.PI / 180.0;
    }
}

